import { useEffect, useState, type ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import { useQuery } from '@tanstack/react-query';
import { PublicBusinessCardSchema, type PublicBusinessCardDto } from '@taqon/contracts';
import taqonLogo from '../../assets/taqon-logo.png';
import { ApiError, getData } from '../../shared/api/client';
import { EASE, rise } from '../../shared/ui/motion';
import { initials } from '../../shared/ui/person';
import { businessCard, cardContact, contactFileName, contactPhoto } from './business-card';

const CARDS = '/api/v1/public/business-cards';

/**
 * An employee's public business card (FRD-HRP-079, CLR-65), the page the business-card code opens:
 * the photo, name, position, company, work e-mail and telephone, and a button that saves the
 * contact with the photo. Rendered before the session check, so it works signed out, for anyone
 * holding the link. A renewed link, or an employee who has left, shows that the card is gone.
 */
export function BusinessCardPage({ token }: { token: string }) {
  const card = useQuery({
    queryKey: ['business-card', token],
    retry: false,
    queryFn: () => getData(`${CARDS}/${token}`, PublicBusinessCardSchema),
  });
  const name = card.data?.name;
  useEffect(() => {
    document.title = name === undefined ? 'Business card' : `${name} — business card`;
  }, [name]);

  return (
    <Box
      component="main"
      id="main"
      sx={{
        minHeight: '100dvh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        py: { xs: 4, sm: 6 },
        bgcolor: 'background.default',
        backgroundImage: [
          'radial-gradient(900px 520px at 12% -10%, rgba(108, 203, 154, 0.26), transparent 70%)',
          'radial-gradient(800px 480px at 100% 110%, rgba(47, 49, 54, 0.12), transparent 70%)',
        ].join(', '),
      }}
    >
      <Paper
        variant="outlined"
        sx={{
          width: '100%',
          maxWidth: 400,
          p: { xs: 3, sm: 4 },
          borderRadius: '24px',
          borderColor: 'rgba(47, 49, 54, 0.08)',
          boxShadow: '0 1px 2px rgba(47, 49, 54, 0.04), 0 24px 48px -16px rgba(47, 49, 54, 0.18)',
          animation: `${rise} 560ms ${EASE} backwards`,
        }}
      >
        {card.isPending ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress aria-label="Loading the business card" />
          </Box>
        ) : card.isError ? (
          <Alert severity={gone(card.error) ? 'info' : 'error'} variant="outlined">
            {gone(card.error)
              ? 'This business card is not available. Its link may have been replaced by a newer one.'
              : `The business card could not be loaded: ${card.error.message}`}
          </Alert>
        ) : (
          <Card card={card.data} token={token} />
        )}
        <Box
          component="img"
          src={taqonLogo}
          alt="TAQON"
          sx={{ display: 'block', height: 28, width: 'auto', mx: 'auto', mt: 4, opacity: 0.8 }}
        />
      </Paper>
    </Box>
  );
}

const gone = (error: Error) => error instanceof ApiError && error.status === 404;

function Card({ card, token }: { card: PublicBusinessCardDto; token: string }) {
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const photo = card.photo ? `${CARDS}/${token}/photo` : undefined;
  const { email, phone } = cardContact(card);

  const save = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      // A contact without the photo is still worth saving when the photo cannot be read.
      const picture = photo === undefined ? undefined : await contactPhoto(photo);
      const text = businessCard(card, card.position, card.company, picture);
      const address = URL.createObjectURL(new Blob([text], { type: 'text/vcard;charset=utf-8' }));
      const anchor = document.createElement('a');
      anchor.href = address;
      anchor.download = contactFileName(card.name);
      anchor.click();
      window.setTimeout(() => {
        URL.revokeObjectURL(address);
      }, 10_000);
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : String(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack spacing={3} sx={{ alignItems: 'center', textAlign: 'center' }}>
      <Avatar
        {...(photo !== undefined ? { src: photo, alt: `Photo of ${card.name}` } : {})}
        sx={{
          width: 144,
          height: 144,
          fontSize: 48,
          fontWeight: 700,
          bgcolor: 'primary.main',
          border: '4px solid #ffffff',
          boxShadow: '0 12px 32px -12px rgba(47, 49, 54, 0.35)',
        }}
      >
        {initials(card.name)}
      </Avatar>
      <Box>
        <Typography component="h1" sx={{ fontSize: '1.5rem', fontWeight: 700, lineHeight: 1.25 }}>
          {card.name}
        </Typography>
        {card.position !== null && (
          <Typography sx={{ mt: 0.5, color: 'text.secondary' }}>{card.position}</Typography>
        )}
        {card.company !== null && (
          <Typography
            sx={{
              mt: 1,
              color: 'primary.dark',
              fontSize: '0.75rem',
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
            }}
          >
            {card.company}
          </Typography>
        )}
      </Box>
      {(phone !== null || email !== null) && (
        <>
          <Divider flexItem />
          <Stack spacing={1.5} sx={{ alignSelf: 'stretch', alignItems: 'flex-start' }}>
            {phone !== null && (
              <Contact icon={<PhoneOutlinedIcon />} href={`tel:${phone.replace(/[^+\d]/g, '')}`}>
                {phone}
              </Contact>
            )}
            {email !== null && (
              <Contact icon={<EmailOutlinedIcon />} href={`mailto:${email}`}>
                {email}
              </Contact>
            )}
          </Stack>
        </>
      )}
      <Button
        variant="contained"
        size="large"
        fullWidth
        startIcon={<PersonAddAlt1Icon />}
        disabled={saving}
        onClick={() => {
          void save();
        }}
      >
        Save contact
      </Button>
      {saveError !== null && (
        <Alert severity="error" variant="outlined" sx={{ alignSelf: 'stretch' }}>
          The contact could not be saved: {saveError}
        </Alert>
      )}
    </Stack>
  );
}

function Contact({ icon, href, children }: { icon: ReactNode; href: string; children: string }) {
  return (
    <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', color: 'text.secondary' }}>
      {icon}
      <Link href={href} underline="hover" sx={{ wordBreak: 'break-all', textAlign: 'left' }}>
        {children}
      </Link>
    </Stack>
  );
}
